'use client';

import React, {
  useCallback,
  useRef,
  useEffect,
  useReducer,
  useMemo,
} from 'react';
import HorizontalSlider from '../../atoms/horizontalSlider';
import TeaserCard from './teaserCard';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { useGetPageList } from '../../../hooks/useGetPageList';

/**
 * Renders a page listing component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.blok - The blok object.

 * @param {string} props.currentStoryInternalId - The internal ID of the current story.
 * @param {boolean} props.hasHorizontalScroll - Indicates if the component has horizontal scroll.
 * @returns {JSX.Element} The rendered component.
 */

const TeaserListing = ({
  blok,
  currentStoryInternalId,
  hasHorizontalScroll,
}) => {
  const pages = useGetPageList(currentStoryInternalId, blok.sort_by_tags);
  const cardContainerRef = useRef(null);

  const teaserList = useMemo(
    () =>
      pages[0]
        ? pages
            .map((page, index) => {
              return index >= blok.listing_limit
                ? null
                : createTeaserParams(page, `teaserUids_${index}`);
            })
            .filter(Boolean)
        : [],
    [pages, blok.listing_limit],
  );

  const horizontalScrollActive =
    (hasHorizontalScroll || blok.horizontal_slider) && teaserList.length > 2;

  const initialState = {
    focusItem: 0,
    cardWidth: 0,
    cardContainerWidth: 0,
    slideLimit: 0,
    markerLimit: 0,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case 'setFocusItem':
        return { ...state, focusItem: action.payload };
      case 'setCardWidth':
        return { ...state, cardWidth: action.payload };
      case 'setCardContainerWidth':
        return { ...state, cardContainerWidth: action.payload };
      case 'setSlideLimit':
        return { ...state, slideLimit: action.payload };
      case 'setMarkerLimit':
        return { ...state, markerLimit: action.payload };
      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { focusItem, cardWidth, cardContainerWidth, slideLimit, markerLimit } =
    state;

  // Function for getting the card width
  const getCardWidth = useCallback((cardWidth) => {
    if (cardWidth) {
      dispatch({ type: 'setCardWidth', payload: cardWidth });
    }
  }, []);

  // Ref for checking if the scroll is programmatic. Used to prevent the scroll event from firing when the user scrolls using buttons.
  const isProgrammaticScroll = useRef(false);

  const handleClick = useCallback(
    (direction) => {
      let nextFocusItem;
      if (direction === 'prev') {
        nextFocusItem = focusItem > 0 ? focusItem - 1 : 0;
      } else if (direction === 'next') {
        nextFocusItem = focusItem < slideLimit ? focusItem + 1 : focusItem;
      }

      isProgrammaticScroll.current = true;
      cardContainerRef.current.scrollTo({
        top: 0,
        left: nextFocusItem * cardWidth,
        behavior: 'smooth',
      });

      setTimeout(() => {
        dispatch({ type: 'setFocusItem', payload: nextFocusItem });
        isProgrammaticScroll.current = false;
      }, 250);
    },
    [focusItem, slideLimit, cardWidth],
  );

  const slideToClickedMarker = useCallback(
    (index) => {
      isProgrammaticScroll.current = true;
      cardContainerRef.current.scrollTo({
        top: 0,
        left: index * cardWidth,
        behavior: 'smooth',
      });

      setTimeout(() => {
        dispatch({ type: 'setFocusItem', payload: index });
        isProgrammaticScroll.current = false;
      }, 250);
    },
    [cardWidth],
  );

  const handleScroll = useCallback(() => {
    if (isProgrammaticScroll.current || !cardContainerRef) return;
    const scrollPosition = cardContainerRef.current.scrollLeft;

    let cardIndex = Math.round(
      scrollPosition /
        (cardContainerRef.current.scrollWidth / teaserList.length),
    );

    dispatch({ type: 'setFocusItem', payload: cardIndex });
  }, [teaserList.length]);

  /**
   * Updates the card container width and slide limit when the card container ref or card width changes.
   */

  const updateCardContainerWidth = useCallback(() => {
    if (cardContainerRef.current) {
      const newCardContainerWidth = cardContainerRef.current.clientWidth;
      if (newCardContainerWidth !== cardContainerWidth) {
        dispatch({
          type: 'setCardContainerWidth',
          payload: newCardContainerWidth,
        });
      }
    }
  }, [cardContainerWidth]);

  useEffect(() => {
    updateCardContainerWidth();

    if (cardWidth > 0 && cardContainerWidth > 0) {
      // "cardsWithinViewport" is the number of wholly visible cards that fit within the viewport.
      const cardsWithinViewport = Math.floor(cardContainerWidth / cardWidth);

      if (
        cardContainerWidth &&
        cardWidth &&
        teaserList.length > cardsWithinViewport
      ) {
        dispatch({
          type: 'setSlideLimit',
          payload: teaserList.length - cardsWithinViewport,
        });
        dispatch({
          type: 'setMarkerLimit',
          payload: teaserList.length + 1 - cardsWithinViewport,
        });
      } else {
        dispatch({
          type: 'setSlideLimit',
          payload: 0,
        });
        dispatch({
          type: 'setMarkerLimit',
          payload: 0,
        });
      }
    }
  }, [
    cardContainerWidth,
    cardWidth,
    teaserList.length,
    updateCardContainerWidth,
  ]);

  /**
   * Updates the card container width when the window is resized.
   * Cleans up the event listener when the component is unmounted.
   */

  useEffect(() => {
    const handleResize = () => {
      updateCardContainerWidth();
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [updateCardContainerWidth]);

  // Tailwind utility classes --- --- --- --- --- --->
  const applyColumns = (elemAmount) => {
    if (elemAmount > 3) {
      return 'grid-flow-col auto-cols-[3vw] sm:auto-cols-[5vw] xl:auto-cols-[6rem] scroll-hide overflow-x-auto no-scrollbar max-w-screen-xl snap-x snap-mandatory';
    }

    if (elemAmount === 3) {
      return 'grid-flow-col auto-cols-[3vw] md:auto-cols-[5vw] lg:grid-cols-12 scroll-hide overflow-x-auto no-scrollbar md:max-w-screen-xl snap-x snap-mandatory';
    }

    return 'grid-cols-12 max-w-screen-xl';
  };

  // >--- --- --- --- --- --- Tailwind utility classes

  return (
    <div
      className={`relative flex flex-col col-span-12`}
      {...storyblokEditable(blok)}
    >
      <div
        className={`w-full h-full ${
          horizontalScrollActive
            ? `flex no-scrollbar overflow-x-auto max-w-screen-xl`
            : 'grid gap-x-4 grid-cols-12 max-w-screen-xl'
        } mx-auto`}
        key={blok._uid}
      >
        {horizontalScrollActive ? (
          <div
            ref={cardContainerRef}
            className={`relative w-full h-full grid gap-x-4 ${applyColumns(
              teaserList.length,
            )}`}
            onScroll={horizontalScrollActive ? handleScroll : null}
          >
            {teaserList.map((teaser, index) => (
              <TeaserCard
                key={teaser._uid}
                blok={teaser}
                inSlider={horizontalScrollActive}
                returnCardWidth={index === 0 ? getCardWidth : null}
              />
            ))}
          </div>
        ) : (
          teaserList.map((teaser, index) => (
            <TeaserCard
              key={teaser._uid}
              blok={teaser}
              inSlider={horizontalScrollActive}
              returnCardWidth={index === 0 ? getCardWidth : null}
            />
          ))
        )}
      </div>
      {/* Render the horizontal slider bar if enabled */}
      {horizontalScrollActive && (
        <HorizontalSlider
          limit={markerLimit}
          focusItem={focusItem}
          slideToClickedMarker={slideToClickedMarker}
          slideLimit={slideLimit}
          handleDirectionClick={handleClick}
        />
      )}
    </div>
  );
};

export default TeaserListing;

// Function for building the PageTeaser blok --- --- --->

/**
 * Creates the parameters for a teaser.
 *
 * @param {Object} page - The page data.
 * @returns {Object} The teaser parameters.
 */
const createTeaserParams = (page, teaserUid) => {
  const content = {
    title: page.node.name,
    text: page.node.field_listing_ingress_string,
    image: {},
  };

  /*
  const title =
    parsedContent.listing_title || page.node.name || 'NO TITLE FOUND';
  const text = parsedContent.listing_ingress || 'NO INGRESS FOUND';

  const imageParams = JSON.parse(page.node.content).listing_image || {};*/

  const teaserParams = {
    _uid: teaserUid,
    title: content.title,
    text: content.text,
    image: content.image,

    button: {
      label: 'Läs mer',
      link: {
        id: page.node.id,
        linktype: 'story',
        fieldtype: 'multilink',
        cached_url: page.node.full_slug || '/',
        color: 'yellow',
      },
    },
  };

  return teaserParams;
};

// >--- --- --- Function for building the PageTeaser blok
